import type React from "react";
import { useEffect, useState } from "react";

import { faComment } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Box, Drawer, IconButton, Stack, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { Outlet, useLocation, useNavigate, useParams } from "react-router-dom";
import styled from "styled-components";
import invariant from "ts-invariant";

import { useGetPatientProfile } from "api/services/patient/hooks/useGetPatientProfile";
import { useProfileContext } from "contexts/ProfileContext";
import { BaseButton } from "shared/atoms/BaseButton";
import { View } from "shared/containers/View";
import type { Breadcrumb } from "shared/containers/View/components/ViewBreadcrumbs";
import { CloseIcon } from "shared/icons";
import { usePageContentOffsetTop } from "shared/styles/usePageContentOffsetTop";
import { DARK_BLUE } from "theme/colors";
import { useIsInRoute, useWindowSize } from "utils/hooks";
import { useCurrentPatient } from "utils/hooks/useCurrentPatient";

import { useMessagesSearchParams } from "../hooks/useMessagesSearchParams";

import { BookingFormPanel } from "./BookingFormPanel";
import { ConversationList } from "./ConversationList";
import { PatientInfoPanel } from "./PatientInfoPanel";

const DRAWER_WIDTH = "400px";

export const MessagesLayout: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { patientId } = useParams();
  const { profile } = useProfileContext();
  const { isBookingFormOpen, isDetailsOpen, closePanel } = useMessagesSearchParams();
  const { patientId: contextPatientID, setPatientId, updateIsCreatable } = useCurrentPatient();
  const { isLargeScreen } = useWindowSize();
  const offsetTop = usePageContentOffsetTop();
  const isBroadcastRoute = useIsInRoute(["broadcast"]);
  const isPatientRoute = useIsInRoute(["patients"]);
  const isChildRoute = isBroadcastRoute || isPatientRoute;
  const showConversationList = isLargeScreen || (!isLargeScreen && !isChildRoute);
  const [additionalBreadcrumbs, setAdditionalBreadcrumbs] = useState<Breadcrumb[]>([]);

  invariant(profile, "Profile should be defined");

  const { data: patientData } = useGetPatientProfile(
    { patientID: Number(patientId) },
    { enabled: isPatientRoute && Boolean(patientId) }
  );

  useEffect(() => {
    if (isBroadcastRoute) {
      setAdditionalBreadcrumbs([{ label: t("messages.buttons.broadcast"), href: location.pathname }]);
      return;
    }

    if (isPatientRoute) {
      const peerName =
        (patientData ? `${patientData.first_name} ${patientData.last_name}` : undefined) ??
        (location.state as { peerName?: string } | null)?.peerName;
      setAdditionalBreadcrumbs(peerName ? [{ label: peerName, href: location.pathname }] : []);
      return;
    }

    setAdditionalBreadcrumbs([]);
  }, [isBroadcastRoute, isPatientRoute, location, patientData, t]);

  useEffect(() => {
    if (patientId) {
      setPatientId(patientId);
      if (contextPatientID) {
        updateIsCreatable();
      }
    } else {
      setPatientId(null);
    }

    return () => {
      setPatientId(null);
    };
  }, [patientId, contextPatientID, setPatientId]);

  const getActionButtons = (): React.ReactElement[] => {
    const buttons: React.ReactElement[] = [];

    if (isLargeScreen || !isChildRoute) {
      buttons.push(
        <BaseButton
          data-testid="broadcast-button"
          text={t("messages.buttons.broadcast")}
          icon="bullhorn"
          onClick={() => navigate("../broadcast")}
          size="large"
        />
      );
    }

    return buttons;
  };

  return (
    <Box
      sx={{
        display: "flex",
        width: "100%",
        paddingTop: `${offsetTop}px`,
      }}
    >
      <StyledView
        title={t("views.messages")}
        additionalBreadcrumbs={additionalBreadcrumbs}
        actionButtons={getActionButtons()}
        $isPanelOpen={isBookingFormOpen || isDetailsOpen}
      >
        <Box
          sx={{
            display: "flex",
            height: "100%",
            borderRadius: "16px",
            position: "relative",
            overflow: "hidden",
          }}
        >
          <SlideContainer $isOpen={showConversationList}>
            <ConversationList />
          </SlideContainer>

          <OutletContainer>
            {isChildRoute ? (
              <Outlet />
            ) : (
              <Stack flex={1} alignItems="center" justifyContent="center" spacing={2}>
                <FontAwesomeIcon icon={faComment} fontSize="40px" color={DARK_BLUE[30]} />
                <Typography variant="subtitle1" color={DARK_BLUE[40]}>
                  {t("messages.no_conversation_selected")}
                </Typography>
              </Stack>
            )}
          </OutletContainer>
        </Box>
      </StyledView>

      <Drawer
        variant="persistent"
        anchor="right"
        open={isBookingFormOpen || isDetailsOpen}
        sx={{
          width: 0,
          "& .MuiDrawer-paper": {
            width: { xs: "100%", md: DRAWER_WIDTH },
            borderLeft: `1px solid ${DARK_BLUE[10]}`,
          },
        }}
      >
        <Stack sx={{ position: isDetailsOpen ? "relative" : "static" }}>
          {isDetailsOpen && (
            <IconButton onClick={() => closePanel("details")} sx={{ position: "absolute", top: 8, right: 8 }}>
              <CloseIcon />
            </IconButton>
          )}
          {isBookingFormOpen && <BookingFormPanel patientId={Number(patientId)} />}
          {isDetailsOpen && <PatientInfoPanel />}
        </Stack>
      </Drawer>
    </Box>
  );
};

const StyledView = styled(View)<{ $isPanelOpen?: boolean }>`
  height: 100vh;
  margin-right: ${({ $isPanelOpen }) => ($isPanelOpen ? DRAWER_WIDTH : "0")};
  transition: ${({ $isPanelOpen }) =>
    $isPanelOpen
      ? "margin-right 225ms cubic-bezier(0.0, 0, 0.2, 1) 0ms"
      : "margin-right 195ms cubic-bezier(0.4, 0, 0.6, 1) 0ms"};

  .view-content-container {
    height: 100%;
    overflow: auto;
  }
`;

const SlideContainer = styled.div<{ $isOpen: boolean }>`
  display: flex;
  position: absolute;
  top: 0;
  left: 0;
  height: 100%;
  width: 100%;
  z-index: ${({ $isOpen }) => ($isOpen ? "2" : "1")};
  transform: ${({ $isOpen }) => ($isOpen ? "translateX(0)" : "translateX(-100%)")};
  transition: transform 0.3s ease-in-out;
  pointer-events: ${({ $isOpen }) => ($isOpen ? "auto" : "none")};

  ${({ theme }) => theme.aboveBreakpoint} {
    position: static;
    width: 275px;
    transform: none;
    z-index: auto;
    pointer-events: auto;
  }
`;

const OutletContainer = styled.div`
  flex: 1;
  display: flex;
  height: 100%;
  width: 100%;
  background-color: ${({ theme }) => theme.colors.white};
  min-width: 0;
`;
